import React, { useCallback, useEffect, useMemo, useState } from 'react';
import _ from 'lodash';

import { useTranslation } from 'react-i18next';
import ServerError from '../../../../../components/ServerError';

import { useUserInfo } from '../../../context/UserInfo';
import { useProjectsQuery } from '../../../__generated__/graphql';
import { useProjectsShowTrackedTime } from '../../../hooks/timeTracking/useProjectsShowTrackedTime';

import Loading from '../Loading';
import { mapProject, mapTimesheet } from '../mapping';

import { ProjectDetailsSidebar } from '../ProjectSidebar/ProjectDetailsSidebar';

import { ProjectsTimesheets } from './ProjectsTimesheets';
import { Project, ProjectViewType, ProjectsFilter, Timesheet } from '../types';
import ProjectsHeader from '../ProjectsHeader';
import { projectsDefaultFilter } from '../ProjectsFiltration';
import { routes } from '../../../routes';
import ProjectsTopPanel from '../ProjectsTopPanel';
import { filterProject, i18prefix, timesheetsRange } from '../helpers';
import SelectionPanel from '../ProjectsTimesheets/SelectionPanel';
import EmptyStateCard from '../../../components/EmptyStateCard';
import {
  useMaxDailyTimeLimitTracking,
  useTimeTrackingRestriction,
} from '../../../hooks/timeTrackingSettings';
import { useFlairPersistentFilter } from '../../../../../hooks/useFlairPersistentFilter';
import { useQueryParameter } from '../../../../../hooks/useQueryParameter';
import {
  toISODateOnly,
  parseDate,
  isSameOrBefore,
  isSameOrAfter,
} from '../../../../../utils/dateUtils';

const Projects: React.FC<React.PropsWithChildren<unknown>> = () => (
  <>
    <ProjectsHeader />
    <Content />
  </>
);

const START_DATE_PARAM = 'startDate';

export const createProjectTimesheetsQueryParams = (startDate: string) => {
  return {
    [START_DATE_PARAM]: startDate,
  };
};

const Content: React.FC<React.PropsWithChildren<unknown>> = () => {
  const { isManagerModeAvailable } = useUserInfo();

  const { timeTrackingRestrictedAbsenceLoading } = useTimeTrackingRestriction();
  const { loading: maxDailyTimeLimitLoading } = useMaxDailyTimeLimitTracking();

  const [selectedStartDate, setSelectedStartDate] =
    useQueryParameter(START_DATE_PARAM);

  const {
    showTrackedTime,
    setShowTrackedTime,
    isTimeTrackingWithProjects,
    isOnlyProjectTracking,
  } = useProjectsShowTrackedTime();

  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(
    null,
  );

  const [filter, setFilter] = useFlairPersistentFilter<ProjectsFilter>({
    defaultFilter: projectsDefaultFilter,
    storageKey: 'projectsTracking',
    createRouteUrl: (queryParams) =>
      routes.projectsTracking.route.withQueryParams(queryParams).create({}),
  });

  const { data, loading, error } = useProjectsQuery({
    variables: timesheetsRange(),
  });

  const timesheets = useMemo(() => {
    if (!data?.myTimeSheets) {
      return [];
    }
    return _.sortBy(data.myTimeSheets.map(mapTimesheet), 'startDate');
  }, [data?.myTimeSheets]);

  useEffect(() => {
    if (!selectedStartDate && timesheets.length > 0) {
      const currentTimesheet = findCurrentTimesheet(timesheets);
      setSelectedStartDate(toISODateOnly(currentTimesheet.startDate));
    }
  }, [selectedStartDate, timesheets, setSelectedStartDate]);

  const handleItemClick = useCallback((project: Project) => {
    setSelectedProjectId(project.id);
  }, []);

  const handleViewTypeChange = useCallback(
    (viewType: ProjectViewType) => {
      setFilter({ ...filter, viewType });
    },
    [filter, setFilter],
  );

  if (error) {
    return <ServerError />;
  }

  if (
    loading ||
    !data ||
    timeTrackingRestrictedAbsenceLoading ||
    maxDailyTimeLimitLoading
  ) {
    return <Loading />;
  }

  if (timesheets.length === 0) {
    return <EmptyTimesheets />;
  }

  if (!data.projects.length) {
    return <EmptyProjects />;
  }

  const projects = data.projects
    .map(mapProject)
    .filter((project) => filterProject(project, filter));

  const selectedTimesheet = selectedStartDate
    ? timesheets.find((timesheet) => {
        const urlDate = parseDate(selectedStartDate);
        return (
          isSameOrBefore(timesheet.startDate, urlDate, { unit: 'day' }) &&
          isSameOrAfter(timesheet.endDate, urlDate, { unit: 'day' })
        );
      })
    : null;

  const selectedItem = projects.find(
    (project) => project.id === selectedProjectId,
  );

  return (
    <>
      <ProjectsTopPanel filter={filter} onFiltrationChange={setFilter}>
        {selectedTimesheet && (
          <SelectionPanel
            timesheets={timesheets}
            selectedTimesheet={selectedTimesheet}
            selectedViewType={filter.viewType}
            showTrackedTime={showTrackedTime}
            onTimesheetChange={(startDate: string) =>
              setSelectedStartDate(startDate)
            }
            onViewTypeChange={handleViewTypeChange}
            onShowTrackedTimeChange={setShowTrackedTime}></SelectionPanel>
        )}
      </ProjectsTopPanel>
      {selectedTimesheet && (
        <ProjectsTimesheets
          key={selectedTimesheet.id} //Using key to rebuild the component, otherwise isDirty is comparing incorrectly
          viewType={filter.viewType}
          projects={[...projects]}
          timesheet={selectedTimesheet}
          showTimeEntries={
            isOnlyProjectTracking
              ? false
              : isTimeTrackingWithProjects
              ? true
              : showTrackedTime
          }
          onProjectClick={handleItemClick}
        />
      )}

      <ProjectDetailsSidebar
        project={selectedItem || null}
        timeTracked={selectedItem?.timeTrackedInMinutes || 0}
        viewMode={isManagerModeAvailable ? 'MANAGER_VIEW' : 'USER_VIEW'}
        onClose={() => setSelectedProjectId(null)}
      />
    </>
  );
};

const EmptyTimesheets: React.FC<React.PropsWithChildren<unknown>> = () => {
  const { t } = useTranslation();

  return <EmptyStateCard title={t(`${i18prefix}.emptyStateForTimesheets`)} />;
};

const EmptyProjects: React.FC<React.PropsWithChildren<unknown>> = () => {
  const { t } = useTranslation();

  return <EmptyStateCard title={t(`${i18prefix}.emptyState`)} />;
};

export default Projects;

function findCurrentTimesheet(timesheets: Timesheet[]) {
  const today = new Date();
  const currentTimesheet = timesheets.find(
    (timesheet) => timesheet.startDate < today && timesheet.endDate > today,
  );

  return currentTimesheet || timesheets[timesheets.length - 1];
}
